import {
  TrackingDetails,
  TrackingDetailsKeys
} from '../../../redux/states/offers/getItemRequestTrackingDetails/getItemRequestTrackingDetails.types'
import status from '../../functions/Status/status.dictionaries'
import { UserRole } from '../../functions/UserRole/userRoleManagement.types'
import { getUserRole } from '../../functions/UserRole/userRoleManagment.functions'
import { TrackingDetailsStatus } from './TrackingDetails.types'

export const getStatuses = (trackingDetails: TrackingDetails, hasLocker = false) => {
  const userRole = getUserRole()

  // Shopper locker-specific tracking steps
  if (hasLocker && userRole === UserRole.shopper) {
    const shopperLockerStatuses: TrackingDetailsStatus[] = [
      {
        id: TrackingDetailsKeys.PurchasedByAdmin,
        name: 'Item Purchased',
        description: 'Item has been purchased.',
        timestamp: trackingDetails?.purchasedByAdmin.timestamp,
        status: trackingDetails?.purchasedByAdmin.status,
        visible: true
      },
      {
        id: TrackingDetailsKeys.ConfirmedReceivedByTraveler,
        name: 'Item Received',
        description: 'Traveler has confirmed receipt of item.',
        timestamp:
          trackingDetails?.confirmedDeliveredToTravelerAddressByAdmin.timestamp ||
          trackingDetails?.confirmedReceivedByTraveler.timestamp,
        status:
          trackingDetails?.confirmedDeliveredToTravelerAddressByAdmin.status ||
          trackingDetails?.confirmedReceivedByTraveler.status,
        visible: true
      },
      {
        id: TrackingDetailsKeys.AwaitingShopperPickupFromLocker,
        name: 'Ready for Pickup',
        subLabel: 'at locker',
        description: 'Item is ready for pickup at the locker.',
        timestamp: trackingDetails?.awaitingShopperPickupFromLocker?.timestamp || null,
        status: trackingDetails?.awaitingShopperPickupFromLocker?.status || false,
        visible: true
      },
      {
        id: TrackingDetailsKeys.ConfirmedDeliveredToShopperByShopper,
        name: 'Item Delivered',
        description: 'Item has been delivered via locker.',
        timestamp:
          trackingDetails?.confirmedDeliveredToShopperViaLocker?.timestamp ||
          trackingDetails?.confirmedDeliveredToShopperByShopper?.timestamp ||
          null,
        status:
          (trackingDetails?.confirmedDeliveredToShopperViaLocker?.status ?? false) ||
          (trackingDetails?.confirmedDeliveredToShopperByShopper?.status ?? false),
        visible: true
      }
    ]

    return shopperLockerStatuses
  }

  // Traveler locker-specific tracking steps
  if (hasLocker && userRole === UserRole.traveler) {
    const travelerLockerStatuses: TrackingDetailsStatus[] = [
      {
        id: TrackingDetailsKeys.PurchasedByAdmin,
        name: 'Item Purchased',
        description: 'Item has been purchased.',
        timestamp: trackingDetails?.purchasedByAdmin.timestamp,
        status: trackingDetails?.purchasedByAdmin.status,
        visible: true
      },
      {
        id: TrackingDetailsKeys.ConfirmedReceivedByTraveler,
        name: 'Item Received',
        description: 'Traveler has confirmed receipt of item.',
        timestamp:
          trackingDetails?.confirmedDeliveredToTravelerAddressByAdmin.timestamp ||
          trackingDetails?.confirmedReceivedByTraveler.timestamp,
        status:
          trackingDetails?.confirmedDeliveredToTravelerAddressByAdmin.status ||
          trackingDetails?.confirmedReceivedByTraveler.status,
        visible: true
      },
      {
        id: TrackingDetailsKeys.ReadyForDropoffAtLocker,
        name: 'Ready for Dropoff',
        subLabel: 'at locker',
        description: 'Item is ready to be dropped off at the locker.',
        timestamp: trackingDetails?.readyForDropoffAtLocker?.timestamp || null,
        status: trackingDetails?.readyForDropoffAtLocker?.status || false,
        visible: true
      },
      {
        id: TrackingDetailsKeys.ConfirmedDeliveredToShopperByTraveler,
        name: 'Item Delivered',
        subLabel: 'to locker',
        description: 'Item has been delivered to the shopper via locker.',
        timestamp:
          trackingDetails?.confirmedDeliveredToShopperViaLocker?.timestamp ||
          trackingDetails?.confirmedDeliveredToShopperByTraveler?.timestamp ||
          null,
        status:
          (trackingDetails?.awaitingShopperPickupFromLocker?.status ?? false) ||
          (trackingDetails?.confirmedDeliveredToShopperViaLocker?.status ?? false) ||
          (trackingDetails?.confirmedDeliveredToShopperByTraveler?.status ?? false),
        visible: true
      }
    ]

    return travelerLockerStatuses
  }

  // Standard tracking steps for non-locker deliveries
  const statuses: TrackingDetailsStatus[] = [
    {
      id: TrackingDetailsKeys.AcceptedByTraveler,
      name: 'Accepted by traveler',
      description: 'Item has been accepted by traveler.',
      timestamp: trackingDetails?.acceptedByTraveler.timestamp,
      status: trackingDetails?.acceptedByTraveler.status,
      visible: false
    },
    {
      id: TrackingDetailsKeys.PurchasedByAdmin,
      name: 'Item Purchased',
      subLabel: 'and on the way to you',
      description: 'Item has been purchased.',
      timestamp: trackingDetails?.purchasedByAdmin.timestamp,
      status: trackingDetails?.purchasedByAdmin.status,
      visible: userRole === UserRole.traveler
    },
    {
      id: TrackingDetailsKeys.PurchasedByAgentAdmin,
      name: 'Item Purchased',
      description: 'Item has been purchased.',
      timestamp: trackingDetails?.purchasedByAgentAdmin.timestamp,
      status: trackingDetails?.purchasedByAgentAdmin.status,
      visible: false
    },
    {
      id: TrackingDetailsKeys.InTransitToTraveler,
      name: 'Item in transit',
      description: 'Item is in transit to traveler.',
      timestamp: trackingDetails?.inTransitToTraveler.timestamp,
      status: trackingDetails?.inTransitToTraveler.status,
      visible: false
    },
    {
      id: TrackingDetailsKeys.DeliveredToTravelerAddress,
      name: 'Item Delivered to Traveler',
      description: 'Item has been delivered to traveler.',
      timestamp: trackingDetails?.deliveredToTravelerAddress.timestamp,
      status: trackingDetails?.deliveredToTravelerAddress.status,
      visible: false
    },
    {
      id: TrackingDetailsKeys.ConfirmedDeliveredToTravelerAddressByAdmin,
      name: 'Item Delivered to Traveler',
      description: 'Admin has confirmed delivery of item to traveler address.',
      timestamp: trackingDetails?.confirmedDeliveredToTravelerAddressByAdmin.timestamp,
      status: trackingDetails?.confirmedDeliveredToTravelerAddressByAdmin.status,
      visible: false
    },
    {
      id: TrackingDetailsKeys.ConfirmedDeliveredToTravelerAddressByAgentAdmin,
      name: 'Item Delivered to Traveler',
      description: 'Agent admin has confirmed delivery of item to traveler address.',
      timestamp: trackingDetails?.confirmedDeliveredToTravelerAddressByAgentAdmin.timestamp,
      status: trackingDetails?.confirmedDeliveredToTravelerAddressByAgentAdmin.status,
      visible: false
    },
    {
      id: TrackingDetailsKeys.ConfirmedReceivedByTraveler,
      name: userRole === UserRole.traveler ? 'Item Received' : 'Traveler Has Item',
      description: 'Traveler has confirmed receipt of item.',
      timestamp: trackingDetails?.confirmedReceivedByTraveler.timestamp,
      status: trackingDetails?.confirmedReceivedByTraveler.status,
      visible: true
    },
    {
      id: TrackingDetailsKeys.ConfirmedInDestinationCountry,
      name: `In Transit`,
      description: 'Traveler has arrived in destination country.',
      timestamp: trackingDetails?.confirmedInDestinationCountry.timestamp,
      status: trackingDetails?.confirmedInDestinationCountry.status,
      visible: false
    },
    {
      id: TrackingDetailsKeys.ConfirmedDeliveredToShopperByTraveler,
      name: 'Item Delivered',
      description: 'Traveler has confirmed delivery of item to the shopper.',
      timestamp: trackingDetails?.confirmedDeliveredToShopperByTraveler.timestamp,
      status: trackingDetails?.confirmedDeliveredToShopperByTraveler.status,
      visible: userRole === UserRole.traveler
    },
    {
      id: TrackingDetailsKeys.ConfirmedDeliveredToShopperByShopper,
      name: 'Item Delivered',
      description: 'Shopper has confirmed delivery of item by the traveler.',
      timestamp: trackingDetails?.confirmedDeliveredToShopperByShopper.timestamp,
      status: trackingDetails?.confirmedDeliveredToShopperByShopper.status,
      visible: userRole === UserRole.shopper
    },
    {
      id: TrackingDetailsKeys.ConfirmedDeliveredToShopperByAdmin,
      name: 'Item Delivered',
      description: 'Admin has confirmed delivery of item to shopper.',
      timestamp: trackingDetails?.confirmedDeliveredToShopperByAdmin.timestamp,
      status: trackingDetails?.confirmedDeliveredToShopperByAdmin.status,
      visible: false
    },
    {
      id: TrackingDetailsKeys.ConfirmedDeliveredToShopperByAgentAdmin,
      name: 'Item Delivered',
      description: 'Agent admin has confirmed delivery of item to shopper.',
      timestamp: trackingDetails?.confirmedDeliveredToShopperByAgentAdmin.timestamp,
      status: trackingDetails?.confirmedDeliveredToShopperByAgentAdmin.status,
      visible: false
    },
    {
      id: TrackingDetailsKeys.Lost,
      name: 'Lost',
      description: 'Item has been lost.',
      timestamp: trackingDetails?.lost.timestamp,
      status: trackingDetails?.lost.status,
      visible: false
    },
    {
      id: TrackingDetailsKeys.OutOfStock,
      name: 'Out of stock',
      description: 'Item is out of stock.',
      timestamp: trackingDetails?.outOfStock.timestamp,
      status: trackingDetails?.outOfStock.status,
      visible: false
    }
  ]

  return statuses
}

export const getCurrentTrackingDetailsStatus = (trackingStatuses: TrackingDetailsStatus[]) => {
  for (let i = 0; i < trackingStatuses.length; i++) {
    if (!trackingStatuses[i].status) {
      return trackingStatuses[i]?.id || trackingStatuses[0].id
    }
  }

  return trackingStatuses[trackingStatuses.length - 1].id
}

export const getNextTrackingDetailsStatus = (trackingStatuses: TrackingDetailsStatus[]) => {
  const currentStatusKey = getCurrentTrackingDetailsStatus(trackingStatuses)
  const currentStatusIndex = trackingStatuses.findIndex((status) => status.id === currentStatusKey)
  const nextStatusIndex = currentStatusIndex + 1
  const nextStatusKey = trackingStatuses[nextStatusIndex].id

  return nextStatusKey
}

export const isStatusTrue = (trackingDetails: TrackingDetails, status: TrackingDetailsKeys) => {
  return trackingDetails[status].status
}

/**
 * Checks if the item is in transit to the shopper.
 *
 * @param status The item tracking's status object, only interested in the status property.
 * @returns True if the item is in transit to the shopper, false otherwise.
 */
export const isItemInTransitToShopper = (statusName: string) => {
  let isInTransit = null

  switch (statusName) {
    case status.deliveryStatus.purchasedByTraveler:
    case status.deliveryStatus.purchasedByAdmin:
    case status.deliveryStatus.purchasedByAgentAdmin:
    case status.deliveryStatus.inTransitToTraveler:
    case status.deliveryStatus.deliveredToTravelerAddress:
    case status.deliveryStatus.confirmedReceivedByTraveler:
    case status.deliveryStatus.confirmedInDestinationCountry:
    case status.deliveryStatus.confirmedDeliveredToShopperByTraveler:
    case status.deliveryStatus.confirmedDeliveredToShopperByShopper:
    case status.deliveryStatus.confirmedDeliveredToTravelerAddressByAdmin:
    case status.deliveryStatus.confirmedDeliveredToShopperByAdmin:
    case status.deliveryStatus.confirmedDeliveredToTravelerAddressByAgentAdmin:
    case status.deliveryStatus.confirmedDeliveredToShopperByAgentAdmin:
      isInTransit = true
      break

    default:
      isInTransit = false
      break
  }

  return isInTransit
}
